/** @jsxRuntime classic */
/** @jsx jsx */
import { Global, css, jsx } from "@emotion/react";

import React, { Component } from "react";
import { connect, useDispatch } from "react-redux";
import type { Dispatch } from "redux";
import type { Store } from "./store";
import { Route, Switch, Redirect } from "react-router";
import { ConnectedRouter, push } from "connected-react-router";
import type { History } from "history";
import { HeaderFetcher } from "./containers/Header";
import Footer from "./containers/Footer";
import SideMenu from "./containers/SideMenu";
import { CartDialog } from "./containers/cart/Cart";
/* eslint-disable no-restricted-imports -- firebase compat: 新たにコードを追加する時は modular API を使う。modular API へ移行したらこのコメントごと削除する */
import firebase from "firebase/compat/app";
import "firebase/compat/functions";
import "firebase/compat/auth";
import "firebase/compat/database";
import "firebase/compat/firestore";
import "firebase/compat/storage";
/* eslint-enable no-restricted-imports */
import { FIREBASE_CONFIG } from "./constants/firebase";
import ModalLoginMethod from "./containers/auth/ModalLoginMethod";
import ModalLogin from "./containers/auth/ModalLogin";
import Loading from "./containers/Loading";
import Error from "./containers/Error";
import Notice from "./containers/Notice";
import ActionModal from "./containers/ActionModal";
import { authActions } from "./modules/auth";
import { RegisterCard } from "./containers/modal/RegisterCard";
import ScrollToTop from "./containers/ScrollToTop";
import Admin from "./containers/admin/Admin";
import { ticketActions } from "./modules/ticket";
import Email from "./containers/Email";
import { adminActions } from "./modules/admin";
import { getQueryObject } from "./utility";
import type { Timestamp } from "@google-cloud/firestore";
import Questionary from "./containers/modal/Questionary";
import { cartActions } from "./modules/cart";
import EventFlowerStandBase from "./containers/event/flowerStand/EventFlowerStandBase";
import AccountTicketBase from "./containers/account/ticket/AccountTicketBase";
import { notificationActions } from "modules/notification";
import { changeStyleWithHosting, isPortalDomain } from "./utility/hosting";
import { setMeta } from "./utility/meta";
import { modalActions } from "modules/modal";

import { getBelongedHosting } from "utility/storage";
import appConfig from "constants/appConfig";
import EventBase from "containers/event/EventBase";
import EventGoodsBase from "containers/event/goods/EventGoodsBase";
import { LoaderLayout } from "components/atoms/loader/LoaderLayout";
import * as Sentry from "@sentry/react";
import { AlertBar } from "components/atoms/AlertBar";
import * as queryString from "query-string";
import { globalStyle } from "./styles";
import type { MaintenanceInfo } from "@spwn/types/firebase/firestore";
import { TenantRouter } from "./features/TenantRouter";
import { PreviewHeader } from "features/PreviewHeader";
import { withRequireLogin } from "./features/withRequireLogin";
import { AccountsApp, isAccountsApp } from "features/accounts/AccountsApp";
import { GoExternalSite } from "components/common/Link";
import {
  pushDataLayer,
  pushDataLayerWithClientBuildUnixTime,
} from "utility/ga";
import { SwitchIfNotLoggedIn } from "features/SwitchIfNotLoggedIn";
import { LanguageInitializer } from "features/i18n/LanguageInitializer";
import { TodayEvent } from "features/todayEvent/TodayEvent";
import { PreFetchRecommendedEvents } from "./features/events/RecommendedEvents/PreFetchRecommendedEvents";
import Box from "@material-ui/core/Box";
import { ThemeProvider } from "@material-ui/core/styles";
import { theme } from "theme/theme";
import { GIFT_LETTER_EVENT_ID } from "./constants/featureFlags";
import { setInitialLanguageToWeglot } from "i18n/weglot";
import { enableWeglot } from "tmp/weglot/enableWeglot";
import { isPopOut } from "containers/streaming/enableCommentPopOut";
import { PATH_PARAMETERS } from "constants/pathParameters";
import { syncNewVideoPlayerImportFlagFromQuery } from "containers/streaming/theoPlayer/newVideoPlayerImportFlag";

// ルートで描画するページは React.lazy で読み込み、開いたページの分だけ JS を取得する
const Top = React.lazy(async () => import("./containers/top/Top"));
const Event = React.lazy(async () => import("./containers/event/Event"));
const Warning = React.lazy(async () => import("./containers/warning/Warning"));
const Question = React.lazy(
  async () => import("./containers/question/Question")
);
const QrcodeReader = React.lazy(
  async () => import("./containers/qrcode/QrcodeReader")
);
const QrcodeReaderSetting = React.lazy(
  async () => import("./containers/qrcode/QrcodeReaderSetting")
);
const QrcodeReaderLog = React.lazy(
  async () => import("./containers/qrcode/QrcodeReaderLog")
);
const Company = React.lazy(async () => import("./containers/company/Company"));
const Contact = React.lazy(async () => import("./containers/contact/Contact"));
const ARAppLogin = React.lazy(
  async () => import("./containers/auth/ARAppLogin")
);
const ARAppLoginRequired = React.lazy(
  async () => import("./containers/auth/ARAppLoginRequired")
);
const ViewerAppLogin = React.lazy(
  async () => import("./containers/auth/ViewerAppLogin")
);
const Settlement = React.lazy(
  async () => import("./containers/settlement/Settlement")
);
const Voucher = React.lazy(async () => import("./containers/voucher"));
const ViewerAppPrivacyPolicy = React.lazy(
  async () => import("./containers/policy/ViewerAppPrivacyPolicy")
);
const StreamingTicketAuthenticator = React.lazy(
  async () => import("./containers/streaming/StreamingTicketAuthenticator")
);
const EventFlowerStandList = React.lazy(
  async () => import("./containers/event/EventFlowerStandList")
);
const StreamingViewTest = React.lazy(
  async () => import("./containers/streaming/StreamingViewTest")
);
const ResetPassword = React.lazy(
  async () => import("./containers/auth/ResetPassword")
);
const Manual = React.lazy(async () => import("./containers/manual/Manual"));
const DebugLogin = React.lazy(
  async () => import("./containers/debug/DebugLogin")
);
const OpenId = React.lazy(async () => import("./containers/openId/OpenId"));
const FanyIdCallback = React.lazy(
  async () => import("./containers/auth/FanyIdCallback")
);
const CrewStreamingFetcher = React.lazy(
  async () => import("features/crew/CrewStreaming")
);
const BrandEventList = React.lazy(async () => ({
  default: (await import("./containers/event/BrandEventList")).BrandEventList,
}));
const EventTicket = React.lazy(async () => ({
  default: (await import("./containers/event/EventTicket")).EventTicket,
}));
const ArchiveEventList = React.lazy(async () => ({
  default: (await import("./containers/event/ArchiveEventList"))
    .ArchiveEventList,
}));
const VodEventListFetcher = React.lazy(async () => ({
  default: (await import("./containers/event/VodEventList"))
    .VodEventListFetcher,
}));
const Account = React.lazy(async () => ({
  default: (await import("./containers/account/Account")).Account,
}));
const AccountEdit = React.lazy(async () => ({
  default: (await import("./containers/account/AccountEdit")).AccountEdit,
}));
const AccountEditPassword = React.lazy(async () => ({
  default: (await import("./containers/account/AccountEditPassword"))
    .AccountEditPassword,
}));
const AccountDelete = React.lazy(async () => ({
  default: (await import("./containers/account/AccountDelete")).AccountDelete,
}));
const LiveEvent = React.lazy(async () => ({
  default: (await import("./containers/live/LiveEvent")).LiveEvent,
}));
const Rule = React.lazy(async () => ({
  default: (await import("./containers/rule/Rule")).Rule,
}));
const BasicTermsOfService = React.lazy(async () => ({
  default: (await import("./features/terms/BasicTermsOfService"))
    .BasicTermsOfService,
}));
const ViewerAppTerms = React.lazy(async () => ({
  default: (await import("./containers/terms/ViewerAppTerms")).ViewerAppTerms,
}));
const AccountTicket = React.lazy(async () => ({
  default: (await import("./containers/account/AccountTicket")).AccountTicket,
}));
const AccountVodTicket = React.lazy(async () => ({
  default: (await import("./containers/account/AccountVodTicket"))
    .AccountVodTicket,
}));
const AccountTicketDigital = React.lazy(async () => ({
  default: (await import("./containers/account/AccountTicketDigital"))
    .AccountTicketDigital,
}));
const AccountBlockedUsers = React.lazy(async () => ({
  default: (await import("./features/blockedUsers/AccountBlockedUsers"))
    .AccountBlockedUsers,
}));
const AccountWatchList = React.lazy(async () => ({
  default: (await import("./features/watchlist/AccountWatchList"))
    .AccountWatchList,
}));
const AccountLottery = React.lazy(async () => ({
  default: (await import("./features/accounts/AccountLottery")).AccountLottery,
}));
const AccountPawUseHistory = React.lazy(async () => ({
  default: (await import("./containers/account/AccountPawUseHistory"))
    .AccountPawUseHistory,
}));
const AccountPawChargeHistory = React.lazy(async () => ({
  default: (await import("./containers/account/AccountPawChargeHistory"))
    .AccountPawChargeHistory,
}));
const AccountCredit = React.lazy(async () => ({
  default: (await import("./containers/account/AccountCredit")).AccountCredit,
}));
const AccountNotificationList = React.lazy(async () => ({
  default: (
    await import("./containers/account/notification/AccountNotificationList")
  ).AccountNotificationList,
}));
const AccountNotification = React.lazy(async () => ({
  default: (
    await import("./containers/account/notification/AccountNotification")
  ).AccountNotification,
}));
const TicketReceive = React.lazy(async () => ({
  default: (await import("./containers/account/TicketReceive")).TicketReceive,
}));
const PawTerms = React.lazy(async () => ({
  default: (await import("./containers/terms/PawTerms")).PawTerms,
}));
const EventFlowerStandItem = React.lazy(async () => ({
  default: (await import("./containers/event/EventFlowerStandItem"))
    .EventFlowerStandItem,
}));
const AccountAddressBase = React.lazy(async () => ({
  default: (await import("./containers/account/AccountAddressBase"))
    .AccountAddressBase,
}));
const EventGoodsHome = React.lazy(async () => ({
  default: (await import("containers/event/goods/EventGoodsHome"))
    .EventGoodsHome,
}));
const EventGoodsItem = React.lazy(async () => ({
  default: (await import("containers/event/goods/EventGoodsItem"))
    .EventGoodsItem,
}));
const EventGoodsItemFromCapsuleId = React.lazy(async () => ({
  default: (
    await import("./containers/event/goods/EventGoodsItemFromCapsuleId")
  ).EventGoodsItemFromCapsuleId,
}));
const VideoBase = React.lazy(async () => ({
  default: (await import("containers/video/VideoBase")).VideoBase,
}));
const VoucherTOFES = React.lazy(async () => ({
  default: (await import("./containers/voucherTOFES/VoucherTOFES"))
    .VoucherTOFES,
}));
const ShopContainer = React.lazy(async () => ({
  default: (await import("./features/ShopContainer")).ShopContainer,
}));
const TenantGoodsContainer = React.lazy(async () => ({
  default: (await import("./features/TenantGoods")).TenantGoodsContainer,
}));
const ShopPreviewContainer = React.lazy(async () => ({
  default: (await import("./features/ShopPreviewContainer"))
    .ShopPreviewContainer,
}));
const TenantGoodsPreview = React.lazy(async () => ({
  default: (await import("./features/TenantGoodsPreview")).TenantGoodsPreview,
}));
const PurchaseContainer = React.lazy(async () => ({
  default: (await import("./features/PurchaseContainer")).PurchaseContainer,
}));
const PurchaseComplete = React.lazy(async () => ({
  default: (await import("./features/PurchaseComplete")).PurchaseComplete,
}));
const CapsuleEventListFetcher = React.lazy(async () => ({
  default: (await import("containers/event/CapsuleEventList"))
    .CapsuleEventListFetcher,
}));
const GoodsEventListFetcher = React.lazy(async () => ({
  default: (await import("containers/event/GoodsEventList"))
    .GoodsEventListFetcher,
}));
const FanclubTermsOfService = React.lazy(async () => ({
  default: (await import("features/terms/FanclubTermsOfService"))
    .FanclubTermsOfService,
}));
const CmsTermsOfService = React.lazy(async () => ({
  default: (await import("features/terms/CmsTermsOfService")).CmsTermsOfService,
}));
const MusicCopyrightGuidelines = React.lazy(async () => ({
  default: (await import("features/terms/MusicCopyrightGuidelines"))
    .MusicCopyrightGuidelines,
}));
const CreatorPrivacyPolicy = React.lazy(async () => ({
  default: (await import("features/terms/CreatorPrivacyPolicy"))
    .CreatorPrivacyPolicy,
}));
const TermsOfService = React.lazy(async () => ({
  default: (await import("features/terms/TermsOfService")).TermsOfService,
}));
const PrivacyPolicy = React.lazy(async () => ({
  default: (await import("./features/terms/PrivacyPolicy")).PrivacyPolicy,
}));
const PortalEventList = React.lazy(async () => ({
  default: (await import("./containers/event/PortalEventList")).PortalEventList,
}));
const RestrictedStreaming = React.lazy(async () => ({
  default: (await import("features/restrictedStreaming/RestrictedStreaming"))
    .RestrictedStreaming,
}));
const ProvisionOfPersonalInfo = React.lazy(async () => ({
  default: (await import("features/accounts/ProvisionOfPersonalInfo"))
    .ProvisionOfPersonalInfo,
}));
const PaymentCompleted = React.lazy(async () => ({
  default: (await import("features/PaymentCompleted")).PaymentCompleted,
}));
const AdminTicketDashboard = React.lazy(async () => ({
  default: (await import("features/ticketDashboard/AdminTicketDashboard"))
    .AdminTicketDashboard,
}));
const AdminSalesDashboard = React.lazy(async () => ({
  default: (await import("features/salesDashboard/AdminSalesDashboard"))
    .AdminSalesDashboard,
}));
const AdminDashboard = React.lazy(async () => ({
  default: (await import("features/adminDashboard/AdminDashboard"))
    .AdminDashboard,
}));
const EventGiftLetter = React.lazy(async () => ({
  default: (await import("containers/event/EventGiftLetter")).EventGiftLetter,
}));
const Search = React.lazy(async () => ({
  default: (await import("./pages/Search")).Search,
}));
const AccountSettlementHistoryV2 = React.lazy(async () => ({
  default: (await import("pages/AccountSettlementHistoryV2"))
    .AccountSettlementHistoryV2,
}));
const AccountSettlementHistoryOrderId = React.lazy(async () => ({
  default: (await import("pages/AccountSettlementHistoryOrderId"))
    .AccountSettlementHistoryOrderId,
}));
const DigitalContentDownload = React.lazy(async () => ({
  default: (await import("pages/DigitalContentDownload"))
    .DigitalContentDownload,
}));

type AppProps = {
  history: History;
  user: firebase.User;
  isLogin: boolean;
  // @ts-expect-error TS7008
  location;
  isAdmin: boolean;
  listenLoginState: () => void;
  addLoginAction: () => void;
  fetchMyTickets: () => void;
  // @ts-expect-error TS7006
  checkMaintenanceUser: (payload) => void;
  getMyCart: () => void;
  watchUserNotifications: () => void;
  fetchPickUpNews: () => void;
  fetchIsAddressRegisterd: () => void;
  checkBelongedHosting: () => void;
  toggleLoginMethod: () => void;
  setLocalTimeDiff: () => void;
  // @ts-expect-error TS7051
  toggleError: (string) => void;
};
type States = {
  isRedirect: boolean;
  isFlowerStandMaintenance: boolean;
  isPawChargeMaintenance: boolean;
  isTicketMaintenance: boolean;
  isGoodsMaintenance: boolean;
  isMaintenance: boolean;
  isLiveEventMaintenance: boolean;
  startAt: Timestamp;
  endAt: Timestamp;
  msg: string;
  liveEventId: string;
};

class App extends Component<AppProps, States> {
  managementWatcher = null;
  // @ts-expect-error TS7006
  constructor(props) {
    super(props);
    firebase.initializeApp(FIREBASE_CONFIG);
    if (window.location.pathname.startsWith("/qrcode-redeemer")) {
      void this.enableFirestorePersistence();
    }
    // Use firebase functions emulator
    if (process.env.REACT_APP_USE_EMULATOR) {
      firebase.auth().useEmulator("http://localhost:9099");
      firebase.firestore().useEmulator("localhost", 8080);
      firebase.functions().useEmulator("localhost", 5001);
      firebase.database().useEmulator("localhost", 9000);
    }
    this.state = {
      isRedirect: false,
      isFlowerStandMaintenance: false,
      isPawChargeMaintenance: false,
      isGoodsMaintenance: false,
      isTicketMaintenance: false,
      isLiveEventMaintenance: false,
      // @ts-expect-error TS2322
      isMaintenance: null,
      // @ts-expect-error TS2322
      startAt: null,
      // @ts-expect-error TS2322
      endAt: null,
      msg: "",
      liveEventId: "",
    };
    this.props.checkBelongedHosting();
    this.trackUser();
  }

  /**
   * enablePersistence を有効にする
   * awaitする必要はない（むしろawaitしないほうが良い）
   * @see https://github.com/firebase/firebase-js-sdk/issues/1697#issuecomment-487386593
   * Firestore は全ての操作を内部的にシーケンシャルに実行する
   * enablePersistence() を先に呼んでいれば、後続の .get() などは persistence の完了を待ってから解決される
   * Promise を await する必要はなく、呼び出し順序だけが重要
   */
  enableFirestorePersistence() {
    firebase
      .firestore()
      .enablePersistence(
        /**
         * 複数タブでspwn.jpにアクセスする (挙動確認時など) 時に、有効にしておく必要がある
         */
        { synchronizeTabs: true }
      )
      .then(() => {
        console.log("Persistence enabled");
      })
      .catch((err) => {
        console.error("Failed to enable persistence:", err);
      });
  }

  sendSignupByEmailEventToGA() {
    // ユーザーのemailにemail verificationを行うための、urlが貼られている.
    // それをclickするとquery parameterにcomplete_email_verification=true付きでspwn.jpのhome画面にredirectされる
    // emailの認証が完了したとみなしてGAにイベントを送信する
    const queryStrings = queryString.parse(window.location.search);
    if (queryStrings.complete_email_verification === "true") {
      pushDataLayerWithClientBuildUnixTime({
        event: "sign_up",
        method: "email",
      });
      /*
       * complete_email_verificationだけを消す。search全体を空にすると、
       * 橋渡し経由のときに隣にいるreturn_urlまで巻き添えになり、
       * 検証後の戻り先を見失う
       */
      delete queryStrings.complete_email_verification;
      this.props.history.replace({
        pathname: window.location.pathname,
        search: queryString.stringify(queryStrings),
      });
    }
  }

  componentDidMount() {
    // TODO: npmモジュールへの移行が完了したらフィーチャーフラグと合わせて削除する
    // CDN同様セッション中フラグを維持するため、ルーティング変化時にsessionStorageへ同期する
    syncNewVideoPlayerImportFlagFromQuery();
    this.isHostnameRedirectRequired();
    this.startManagementWatching();
    this.props.listenLoginState();
    this.props.setLocalTimeDiff();
    this.props.fetchPickUpNews();
    // /qrcode-redeemer は firebase.json の rewrites で動く onQrCodeRedeemerPage によって title を設定しているため、上書きしないようにする
    if (!window.location.pathname.startsWith("/qrcode-redeemer")) {
      setMeta();
    }
    this.sendSignupByEmailEventToGA();
    if (enableWeglot()) {
      setInitialLanguageToWeglot();
    }
  }
  componentDidUpdate(prevProps: AppProps) {
    this.trackUser();
    // execute once logined
    if (
      !prevProps.isLogin &&
      this.props.user.uid &&
      this.props.isLogin === true
    ) {
      // user由来のwatchを開始 (myCart, notification
      this.props.getMyCart();
      this.props.watchUserNotifications();
      // fetch user info (address, tickets)
      this.props.fetchIsAddressRegisterd();
      this.props.fetchMyTickets();
      // admin checker
      const queryObject = getQueryObject(this.props.location);
      this.props.checkMaintenanceUser(queryObject);
      // set uid to cookies for contact
      window.document.cookie = `uid=${this.props.user.uid};domain=spwn.jp;secure`;
      window.document.cookie = `last-domain=${window.location.hostname};domain=spwn.jp;secure`;
    }
    const queryStrings = queryString.parse(window.location.search);
    if ("errorCode" in queryStrings) {
      this.props.toggleError(queryStrings.msg);
    }
  }

  /**
   * リダイレクトフラグをセットする
   */
  isHostnameRedirectRequired() {
    this.setState({
      isRedirect: this.isUnAvailableFirebaseSubDomain(),
    });
  }

  /**
   * firebase hosting登録時に自動作成されるサブドメイン判定
   * ※ このサブドメインは cors の関係で利用できないAPIが多い
   */
  isUnAvailableFirebaseSubDomain() {
    const domain = window.location.hostname;
    // spwn-balus.web.app, spwn-balus.firebaseapp.com, etc
    if (domain.endsWith("web.app") || domain.endsWith("firebaseapp.com")) {
      return true;
    }
    return false;
  }

  startManagementWatching() {
    const watcher = firebase
      .firestore()
      .collection("/management")
      .onSnapshot((snapshot) => {
        // @ts-expect-error TS2322
        let maintenanceInfo: MaintenanceInfo = null;
        let flags = null;
        for (let i = 0; i < snapshot.docs.length; ++i) {
          // @ts-expect-error TS2532
          switch (snapshot.docs[i].id) {
            case "flags": {
              // @ts-expect-error TS2532
              flags = snapshot.docs[i].data();
              break;
            }
            case "maintenanceInfo": {
              // @ts-expect-error TS2532
              const data: any = snapshot.docs[i].data(); // eslint-disable-line @typescript-eslint/no-explicit-any
              maintenanceInfo = data;
              break;
            }
          }
        }
        if (
          flags === undefined ||
          maintenanceInfo === undefined ||
          flags === null ||
          maintenanceInfo == null
        ) {
          console.log("ASDFGHJK");
          return;
        }
        this.setState({
          isFlowerStandMaintenance: maintenanceInfo.isFlowerStandMaintenance,
          isPawChargeMaintenance: maintenanceInfo.isPawChargeMaintenance,
          isTicketMaintenance: maintenanceInfo.isTicketMaintenance,
          isGoodsMaintenance: maintenanceInfo.isGoodsMaintenance,
          isMaintenance: maintenanceInfo.isMaintenance,
          isLiveEventMaintenance: maintenanceInfo.isLiveEventMaintenance,
          // @ts-expect-error TS2322
          startAt: maintenanceInfo.startAt,
          // @ts-expect-error TS2322
          endAt: maintenanceInfo.endAt,
          msg: maintenanceInfo.msg,
          liveEventId: maintenanceInfo.liveEventId,
        });
      });
    // @ts-expect-error TS2322
    this.managementWatcher = watcher;
  }

  componentWillUnmount() {
    if (this.managementWatcher !== null) {
      // @ts-expect-error TS2349
      this.managementWatcher();
    }
  }

  isInMaintenanceTimePeriod = () => {
    return (
      this.state.startAt !== null &&
      this.state.endAt !== null &&
      this.state.startAt.seconds * 1000 <= Date.now() &&
      this.state.endAt.seconds * 1000 >= Date.now()
    );
  };

  trackUser = () => {
    // Google Analytics に uid を送信する
    pushDataLayer({
      uid: this.props.user.uid,
      domain: getBelongedHosting(),
    });

    // Sentry にユーザー情報を送信する
    Sentry.setUser({
      id: this.props.user.uid,
      // @ts-expect-error TS2322
      username: this.props.user.displayName,
      // @ts-expect-error TS2322
      email: this.props.user.email,
      ip_address: "{{auto}}",
    });
  };

  render() {
    const { history, isLogin } = this.props;

    /**
     * firebase hostingで自動作成された、利用できないホストにアクセスしてきたユーザを正式なホストにリダイレクトする
     */
    if (this.state.isRedirect) {
      // firebase config の 認証ドメインにリダイレクトする
      const { authDomain } = FIREBASE_CONFIG;
      return (
        <GoExternalSite
          externalLink={`https://${authDomain}`}
          caution={
            // eslint-disable-next-line no-restricted-syntax -- i18n 未対応
            "このページのドメインが移行されたため、自動的に新しいドメインに遷移します。 / The domain of this page has been migrated and will automatically redirect to the new domain."
          }
        />
      );
    }

    const classesApp = AppDesign();

    if (isAccountsApp()) {
      /**
       * アカウントアプリケーションはportalのログイン機能を流用する
       */
      return <AccountsApp history={history} />;
    }

    if (this.state.isMaintenance === null) {
      return (
        <div id="container" css={classesApp.container}>
          <ConnectedRouter history={history}>
            <HeaderFetcher />
            <LoaderLayout />
            <Footer />
          </ConnectedRouter>
        </div>
      );
    }

    if (
      this.state.isMaintenance &&
      this.isInMaintenanceTimePeriod() &&
      !this.props.isAdmin
    ) {
      return <Admin />;
    }

    const isFlowerStandMaintenance =
      this.state.isFlowerStandMaintenance &&
      this.isInMaintenanceTimePeriod() &&
      !this.props.isAdmin;
    // const isPawChargeMaintenance =
    //   this.state.isPawChargeMaintenance &&
    //   this.isInMaintenanceTimePeriod() &&
    //   !this.props.isAdmin;
    const isGoodsMaintenance =
      this.state.isGoodsMaintenance &&
      this.isInMaintenanceTimePeriod() &&
      !this.props.isAdmin;
    const isTicketMaintenance =
      this.state.isTicketMaintenance &&
      this.isInMaintenanceTimePeriod() &&
      !this.props.isAdmin;
    const { isLiveEventMaintenance } = this.state;

    console.log(process.env.REACT_APP_FB_PROJECT_ID);

    return (
      <React.Fragment>
        <LanguageInitializer />
        <ThemeProvider theme={theme}>
          <ConnectedRouter history={history}>
            <Global styles={globalStyle} />

            <Box
              component={"section"}
              display={"flex"}
              flexDirection={"column"}
              css={css`
                min-height: 100vh; /* fallback */
                @supports (min-height: 100dvh) {
                  min-height: 100dvh;
                }
              `}
            >
              {!isPopOut() && <HeaderFetcher />}

              <div
                id="container"
                css={classesApp.container}
                style={{
                  flex: "1 1 auto",
                }}
              >
                {this.props.location.pathname.includes("/shop-preview") && (
                  <PreviewHeader />
                )}

                {this.props.location.pathname.indexOf("/streaming") === -1 && (
                  <AlertBar />
                )}

                {!this.props.location.pathname.includes("/streaming") &&
                  !this.props.location.pathname.includes("/account") &&
                  !this.props.location.pathname.includes("/streams/p/") && (
                    <TodayEvent />
                  )}

                <SideMenu />

                <ScrollToTop>
                  <CartDialog />
                  <React.Suspense fallback={<LoaderLayout />}>
                    <Switch>
                      <Route
                        exact
                        path="/payment-completed"
                        component={PaymentCompleted}
                      />
                      <Route
                        exact
                        path="/"
                        /**
                         * @see https://v5.reactrouter.com/web/api/Route/render-func
                         */
                        render={() => (
                          <Top
                            isAdmin={this.props.isAdmin}
                            isLiveEventMaintenance={isLiveEventMaintenance}
                            maintenanceLiveEventId={
                              this.props.isAdmin && isLiveEventMaintenance
                                ? this.state.liveEventId
                                : ""
                            }
                          />
                        )}
                      />
                      <TenantRouter
                        exact
                        path="/shop"
                        component={ShopContainer}
                      />
                      <TenantRouter
                        exact
                        path="/shop-preview"
                        component={withRequireLogin(ShopPreviewContainer)}
                      />
                      <TenantRouter
                        exact
                        path="/shop/goods/:goodsId"
                        component={TenantGoodsContainer}
                      />
                      <TenantRouter
                        exact
                        path="/shop-preview/goods/:goodsId"
                        component={withRequireLogin(TenantGoodsPreview)}
                      />
                      <Route
                        exact
                        path="/events"
                        component={
                          isPortalDomain() ? PortalEventList : BrandEventList
                        }
                      />
                      <Route path="/events/:eventId">
                        {/* please include component with `/events/:eventId/~` path name basically */}
                        <EventBase>
                          <Switch>
                            <Route
                              exact
                              path="/events/:eventId"
                              component={Event}
                            />
                            <Route
                              exact
                              path={`/events/${GIFT_LETTER_EVENT_ID}/gift-letter`}
                              component={EventGiftLetter}
                            />
                            <PrivateRoute
                              exact
                              path="/events/:eventId/streaming"
                              component={StreamingTicketAuthenticator}
                              authed={isLogin}
                            />
                            <PrivateRoute
                              exact
                              path="/events/:eventId/ticket"
                              component={
                                isTicketMaintenance ? Admin : EventTicket
                              }
                              authed={isLogin}
                            />
                            <PrivateRoute
                              exact
                              path="/events/:eventId/tickets/:ticketId"
                              component={
                                isTicketMaintenance ? Admin : EventTicket
                              }
                              authed={isLogin}
                            />
                            <Route path="/events/:eventId/flower-stand">
                              <EventFlowerStandBase>
                                <Switch>
                                  <PrivateRoute
                                    exact
                                    path="/events/:eventId/flower-stand"
                                    component={
                                      isFlowerStandMaintenance
                                        ? Admin
                                        : EventFlowerStandList
                                    }
                                    authed={isLogin}
                                  />
                                  <PrivateRoute
                                    exact
                                    path="/events/:eventId/flower-stand/:flowerStandName"
                                    component={
                                      isFlowerStandMaintenance
                                        ? Admin
                                        : EventFlowerStandItem
                                    }
                                    authed={isLogin}
                                  />
                                </Switch>
                              </EventFlowerStandBase>
                            </Route>
                            <Route path="/events/:eventId/goods">
                              <EventGoodsBase>
                                <Switch>
                                  <Route
                                    exact
                                    path="/events/:eventId/goods"
                                    component={
                                      isGoodsMaintenance
                                        ? Admin
                                        : EventGoodsHome
                                    }
                                  />
                                  <Route
                                    exact
                                    path="/events/:eventId/goods/:goodsName"
                                    component={
                                      isGoodsMaintenance
                                        ? Admin
                                        : EventGoodsItem
                                    }
                                  />
                                </Switch>
                              </EventGoodsBase>
                            </Route>
                            <Route
                              exact
                              path="/events/:eventId/capsule/:capsuleId"
                              component={
                                isGoodsMaintenance
                                  ? Admin
                                  : EventGoodsItemFromCapsuleId
                              }
                            />
                          </Switch>
                        </EventBase>
                      </Route>
                      <Redirect
                        from="/_events/:eventId"
                        to={{
                          pathname: "/events/:eventId",
                          search: document.location.search,
                        }}
                      />
                      <Route
                        exact
                        path="/archive-events"
                        component={ArchiveEventList}
                      />
                      <Route
                        exact
                        path="/vod-events"
                        component={VodEventListFetcher}
                      />
                      <Route
                        exact
                        path="/capsule"
                        component={CapsuleEventListFetcher}
                      />
                      <Route
                        exact
                        path="/goods"
                        component={GoodsEventListFetcher}
                      />
                      <Route exact path="/search">
                        <Search email={this.props.user.email} />
                      </Route>
                      <PrivateRoute
                        exact
                        path="/live-event"
                        component={
                          !isLiveEventMaintenance || this.props.isAdmin
                            ? LiveEvent
                            : Top
                        }
                        authed={isLogin}
                      />
                      <PrivateRoute
                        exact
                        path="/videos/:videoId"
                        component={VideoBase}
                        authed={isLogin}
                      />
                      <PrivateRoute
                        exact
                        path="/account"
                        component={Account}
                        authed={isLogin}
                      />
                      <Route path="/account/ticket">
                        <AccountTicketBase>
                          <Switch>
                            <PrivateRoute
                              exact
                              path="/account/ticket"
                              component={AccountTicket}
                              authed={isLogin}
                            />
                            <PrivateRoute
                              exact
                              path="/account/ticket/vod"
                              component={AccountVodTicket}
                              authed={isLogin}
                            />
                            <PrivateRoute
                              exact
                              path="/account/ticket/:eventId"
                              component={AccountTicketDigital}
                              authed={isLogin}
                            />
                          </Switch>
                        </AccountTicketBase>
                      </Route>
                      <PrivateRoute
                        exact
                        path="/account/ticket-receive"
                        component={TicketReceive}
                        authed={isLogin}
                      />
                      <PrivateRoute
                        exact
                        path="/account/lottery"
                        component={AccountLottery}
                        authed={isLogin}
                      />
                      <PrivateRoute
                        exact
                        path="/account/settlement/history"
                        component={AccountSettlementHistoryV2}
                        authed={isLogin}
                      />
                      <PrivateRoute
                        exact
                        path={`/account/settlement/history/:${PATH_PARAMETERS["/account/settlement/history/:orderId"]}`}
                        component={AccountSettlementHistoryOrderId}
                        authed={isLogin}
                      />
                      <PrivateRoute
                        exact
                        path="/account/settlement/history/:orderId/items/:lineItemId/downloads"
                        component={DigitalContentDownload}
                        authed={isLogin}
                      />
                      <PrivateRoute
                        exact
                        path="/account/watchlist"
                        component={AccountWatchList}
                        authed={isLogin}
                      />
                      <PrivateRoute
                        exact
                        path="/account/paw-charge-history"
                        component={AccountPawChargeHistory}
                        authed={isLogin}
                      />
                      <PrivateRoute
                        exact
                        path="/account/paw-use-history"
                        component={AccountPawUseHistory}
                        authed={isLogin}
                      />
                      <PrivateRoute
                        exact
                        path="/account/edit"
                        component={AccountEdit}
                        authed={isLogin}
                      />
                      <PrivateRoute
                        exact
                        path="/account/edit/delete"
                        component={AccountDelete}
                        authed={isLogin}
                      />
                      <PrivateRoute
                        exact
                        path="/account/edit/password"
                        component={AccountEditPassword}
                        authed={isLogin}
                      />
                      <PrivateRoute
                        exact
                        path="/account/blocklist"
                        component={AccountBlockedUsers}
                        authed={isLogin}
                      />
                      <PrivateRoute
                        exact
                        path="/account/credit"
                        component={AccountCredit}
                        authed={isLogin}
                      />
                      <PrivateRoute
                        exact
                        path="/account/address"
                        component={AccountAddressBase}
                        authed={isLogin}
                      />
                      <PrivateRoute
                        exact
                        path="/account/notifications"
                        component={AccountNotificationList}
                        authed={isLogin}
                      />
                      <PrivateRoute
                        exact
                        path="/account/notifications/:notificationId"
                        component={AccountNotification}
                        authed={isLogin}
                      />
                      <PrivateRoute
                        exact
                        path="/account/provision-of-personal-info"
                        component={ProvisionOfPersonalInfo}
                        authed={isLogin}
                      />
                      <PrivateRoute
                        exact
                        path="/settlement"
                        component={
                          isGoodsMaintenance || isTicketMaintenance
                            ? Admin
                            : Settlement
                        }
                        authed={isLogin}
                      />
                      <PrivateRoute
                        exact
                        path="/purchase"
                        component={PurchaseContainer}
                        authed={isLogin}
                      />
                      <PrivateRoute
                        exact
                        path="/purchase-complete"
                        component={PurchaseComplete}
                        authed={isLogin}
                      />
                      {/* <PrivateRoute exact path='/settlement-renewal' component={isGoodsMaintenance|| isTicketMaintenance? Admin: SettlementRenewal} authed={isLogin} /> */}
                      <Route exact path="/voucher" component={Voucher} />
                      {/* HOTFIX https://github.com/balus-co-ltd/spwn/issues/297 */}
                      <Route
                        exact
                        path="/voucher-210829-TOFES"
                        component={VoucherTOFES}
                      />
                      <Route exact path="/event-notes" component={Warning} />
                      <Route exact path="/faq" component={Question} />
                      <Route exact path="/company" component={Company} />
                      <Route exact path="/law" component={Rule} />
                      <Route
                        exact
                        path="/basic-terms-of-service"
                        component={BasicTermsOfService}
                      />
                      <Route
                        exact
                        path="/fanclub-terms-of-service"
                        component={FanclubTermsOfService}
                      />
                      <Route
                        exact
                        path="/cms-terms-of-service"
                        component={CmsTermsOfService}
                      />
                      <Route
                        exact
                        path="/music-copyright-guidelines"
                        component={MusicCopyrightGuidelines}
                      />
                      <Route
                        exact
                        path="/creator-privacy-policy"
                        component={CreatorPrivacyPolicy}
                      />
                      <Route
                        exact
                        path="/terms-of-service"
                        component={TermsOfService}
                      />
                      <Route
                        exact
                        path="/privacy-policy"
                        component={PrivacyPolicy}
                      />
                      <Route
                        exact
                        path="/terms-of-service-app"
                        component={ViewerAppTerms}
                      />
                      <Route exact path="/contact" component={Contact} />
                      <Route
                        exact
                        path="/privacy-policy-app"
                        component={ViewerAppPrivacyPolicy}
                      />
                      <Route exact path="/paw-terms" component={PawTerms} />
                      <Route exact path="/manual" component={Manual} />
                      <Route
                        exact
                        path="/streaming-test"
                        component={StreamingViewTest}
                      />
                      <Route
                        exact
                        path="/ar-app-login-ehgeywoyhoebavro"
                        component={ARAppLogin}
                      />{" "}
                      {/** only use for ar app login */}
                      <Route
                        exact
                        path="/ar-app-login-required"
                        component={ARAppLoginRequired}
                      />{" "}
                      {/** only use for ar app login */}
                      <Route
                        exact
                        path="/viewer-app-login"
                        component={ViewerAppLogin}
                      />{" "}
                      {/** only use for ar app login */}
                      <Route
                        exact
                        path="/reset-password"
                        component={ResetPassword}
                      />
                      <Route exact path="/openid" component={OpenId} />
                      <Route
                        exact
                        path="/fanyid-callback"
                        component={FanyIdCallback}
                      />
                      {appConfig.logDebug ? (
                        <Route exact path="/debug" component={DebugLogin} />
                      ) : null}
                      <Route
                        exact
                        // NOTE: クライアントに共有される「会場チケットもぎりマニュアル」にURLの記載があるため、パスを変更する際はマニュアルの変更が必要
                        path="/qrcode-redeemer"
                        component={QrcodeReader}
                      />
                      <Route
                        exact
                        path="/qrcode-redeemer-settings"
                        component={QrcodeReaderSetting}
                      />
                      <Route
                        exact
                        path="/qrcode-redeemer/log"
                        component={QrcodeReaderLog}
                      />
                      <Route
                        exact
                        path="/streams/:streamingId"
                        /**
                         * ログインをしていなかったら、ログインモーダルを表示するコンポーネント
                         * FIXME 今後このコンポーネントを採用する場合は、経由させるコンポーネントを作成するなどリファクタリングを行う。
                         */
                        component={() => (
                          <SwitchIfNotLoggedIn switchComponent={<Loading />}>
                            <CrewStreamingFetcher />
                          </SwitchIfNotLoggedIn>
                        )}
                      />
                      {/* 関係者限定配信ページ */}
                      <Route
                        exact
                        path="/streams/p/:videoId"
                        component={RestrictedStreaming}
                      />
                      <PrivateRoute
                        exact
                        path="/admin"
                        component={AdminDashboard}
                        authed={isLogin}
                      />
                      {/* チケットのもぎり状況のダッシュボードは管理者だけ閲覧できる */}
                      <PrivateRoute
                        exact
                        path="/admin/tickets"
                        component={AdminTicketDashboard}
                        authed={isLogin}
                      />
                      {/* 管理者用の売り上げダッシュボード(APIで認可判定) */}
                      <PrivateRoute
                        exact
                        path="/admin/sales"
                        component={AdminSalesDashboard}
                        authed={isLogin}
                      />
                      <Route exact component={Top} />
                    </Switch>
                  </React.Suspense>
                  <Loading />
                  <Email />
                  <Error />
                  <Notice />
                  <PreFetchRecommendedEvents />
                  <ActionModal />
                  <ModalLoginMethod />
                  <ModalLogin />
                  <RegisterCard />
                  <Questionary />
                </ScrollToTop>
              </div>

              <Footer />
            </Box>
          </ConnectedRouter>
        </ThemeProvider>
      </React.Fragment>
    );
  }
}

/**
 * need login router
 */
// @ts-expect-error TS7031
const PrivateRoute = ({ component: Component, authed, ...rest }) => {
  const dispatch = useDispatch();

  if (authed === null || typeof authed === "undefined") {
    // ログインが確認できるまではrenderしない
    return <React.Fragment></React.Fragment>;
  }
  const parentProps = !rest.parentProps ? {} : rest.parentProps;
  if (authed === false) {
    dispatch(modalActions.toggleLoginMethod());
    // TODO@later 直リンクでPrivateRouteのコンポーネントに飛ぶとTopがrenderされない問題
    dispatch(push(changeStyleWithHosting().commonSettings.route.home));
    return null;
  }
  return (
    // prettier-ignore
    <Route
      {...rest}
      render={(props) =>
      // topにリダイレクトしたらログインpopupが出るようにする
      {
        return (authed === true ? (
          <Component {...props} openLoginModal={false} {...parentProps} />
        ) : (
          <Redirect
            to={{
              pathname: "/",
              state: { from: props.location, openLoginModal: true },
            }}
          />
        ))
      }
      }
    />
  );
};

const mapStateToProps = (state: Store) => {
  return {
    user: state.auth.user,
    isLogin: state.auth.isLogin,
    location: state.router.location,
    isAdmin: state.admin.isAdmin,
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => {
  return {
    listenLoginState: () => {
      dispatch(authActions.listenLoginState.started());
    },
    // @ts-expect-error TS7006
    addLoginAction: (action) => {
      dispatch(authActions.addLoginAction(action));
    },
    fetchMyTickets: () => {
      dispatch(ticketActions.fetchMyTickets.started({ callConfirmTran: true }));
    },
    // @ts-expect-error TS7006
    checkMaintenanceUser: (payload) => {
      dispatch(adminActions.checkMaintenanceUser.started(payload));
    },
    getMyCart: () => {
      dispatch(cartActions.getMyCart.started({}));
    },
    watchUserNotifications: () => {
      dispatch(notificationActions.watchUserNotifications.started());
    },
    fetchPickUpNews: () => {
      dispatch(notificationActions.fetchPickUpNews.started());
    },
    fetchIsAddressRegisterd: () => {
      dispatch(authActions.fetchIsAddressRegisterd.started());
    },
    checkBelongedHosting: () => {
      dispatch(adminActions.checkBelongedHosting());
    },
    toggleLoginMethod: () => {
      dispatch(modalActions.toggleLoginMethod());
    },
    setLocalTimeDiff: () => {
      dispatch(authActions.setLocalTimeDiff.started({}));
    },
    // @ts-expect-error TS7006
    toggleError: (msg) => {
      dispatch(modalActions.toggleError({ msg }));
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(App);

const AppDesign = () => {
  const belongedHosting = getBelongedHosting();
  const hostingBaseStyleMap = changeStyleWithHosting().baseStyles;

  const backgroundImage = hostingBaseStyleMap.backgroundImage
    ? "url(" + hostingBaseStyleMap.backgroundImage + ")"
    : "none";

  if (belongedHosting === "vtuber1") {
    return {
      container: css`
        background-color: ${hostingBaseStyleMap.baseColor};
        background-image: ${backgroundImage};
        background-size: auto 1500px;
        background-position: 0% 100%;
        @media screen and (min-width: 768px) {
          background-size: auto 6000px;
          background-repeat: repeat-y;
          background-position: 50% 0px;
        }
      `,
    };
  }
  if (belongedHosting === "balus-1") {
    return {
      container: css`
        background-color: ${hostingBaseStyleMap.baseColor};
        background-image: ${backgroundImage};
        background-size: 100% auto;
        background-repeat: repeat-y;
      `,
    };
  }
  return {
    container: css`
      background-color: ${hostingBaseStyleMap.baseColor};
      background-image: ${backgroundImage};
      background-size: 500px auto;
    `,
  };
};
